// Pure role-hierarchy logic - deliberately zero imports, so it can be unit
// tested without pulling in Vuetify/Vuex/the router the way importing
// anything from helpers.js does (every named export in that file executes
// the same module-load chain). Mirrors calendesk-api's Role::hierarchy() /
// User::highestRoleLevel() / AdminUserController::assertCanManage() - keep
// both in sync (project/app/Models/Role.php, project/app/Models/User.php).

export const ROLE_SLUGS = {
  root: "root",
  admin: "admin",
  manager: "manager",
  employee: "employee",
  employee_basic: "employee_basic",
};

// Highest privilege first.
export const ROLE_HIERARCHY = [
  ROLE_SLUGS.root,
  ROLE_SLUGS.admin,
  ROLE_SLUGS.manager,
  ROLE_SLUGS.employee,
  ROLE_SLUGS.employee_basic,
];

export function userHasRole(user, role) {
  if (user) {
    let roles = [];

    if (user.roles) {
      roles = user.roles.map((role) => role.name);
    }

    return roles.includes(role);
  }

  return false;
}

// Lowest index = most privileged. A user with no admin-side role at all
// (a plain client account), or only roles this hierarchy doesn't know
// about, ranks below every real role.
function getRoleHierarchyLevel(user) {
  if (!user || !user.roles) {
    return ROLE_HIERARCHY.length;
  }

  const levels = user.roles
    .map((role) => ROLE_HIERARCHY.indexOf(role.name))
    .filter((level) => level !== -1);

  return levels.length > 0 ? Math.min(...levels) : ROLE_HIERARCHY.length;
}

// Mirrors AdminUserController::assertCanManage on the backend: an actor may
// only manage accounts strictly below their own role; root can manage
// anyone; everyone can manage their own account (self-service).
export function canManageUser(actorUser, targetUser) {
  if (!actorUser || !targetUser) {
    return false;
  }

  if (actorUser.id === targetUser.id) {
    return true;
  }

  if (userHasRole(actorUser, ROLE_SLUGS.root)) {
    return true;
  }

  // A plain customer (no role, no direct permission) is manageable by any
  // staff account, including one that holds permissions without a role and
  // would otherwise rank equal to the customer.
  if (isPlainCustomer(targetUser)) {
    return true;
  }

  return getRoleHierarchyLevel(targetUser) > getRoleHierarchyLevel(actorUser);
}

function isPlainCustomer(user) {
  return (
    getRoleHierarchyLevel(user) === ROLE_HIERARCHY.length &&
    !(user.permissions && user.permissions.length > 0)
  );
}
